import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Lightbulb, Rocket, CheckCircle } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80';
const TEAM_IMG = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80';
const OFFICE_IMG = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80';

// Marquee component
const Marquee = ({ text, reverse = false }) => (
  <div className="overflow-hidden py-4 border-y border-gray-200">
    <div className="flex" style={{ animation: `marquee 25s linear infinite ${reverse ? 'reverse' : ''}` }}>
      {[...Array(10)].map((_, i) => (
        <span key={i} className="marquee-item">{text}</span>
      ))}
    </div>
  </div>
);

export function GroupPage() {
  const { i18n } = useTranslation();

  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  const pillars = [
    {
      number: '01',
      title: getText('Concevoir', 'تصميم', 'Design'),
      desc: getText(
        "De l'idée au prototype, nous transformons votre vision en réalité tangible avec une approche centrée sur l'utilisateur.",
        'من الفكرة إلى النموذج الأولي، نحول رؤيتك إلى واقع ملموس بنهج يركز على المستخدم.',
        'From idea to prototype, we transform your vision into tangible reality with a user-centered approach.'
      ),
      icon: Lightbulb
    },
    {
      number: '02',
      title: getText('Industrialiser', 'تصنيع', 'Industrialize'),
      desc: getText(
        "Sourcing optimisé, production maîtrisée et contrôle qualité rigoureux pour des produits d'excellence.",
        'توريد محسن وإنتاج متقن ومراقبة جودة صارمة لمنتجات متميزة.',
        'Optimized sourcing, controlled production and rigorous quality control for excellent products.'
      ),
      icon: Target
    },
    {
      number: '03',
      title: getText('Déployer', 'نشر', 'Deploy'),
      desc: getText(
        "Accompagnement complet jusqu'à la mise en marché : logistique, distribution et support technique.",
        'مرافقة كاملة حتى طرح السوق: اللوجستيات والتوزيع والدعم الفني.',
        'Complete support until market launch: logistics, distribution and technical support.'
      ),
      icon: Rocket
    }
  ];

  const values = [
    { 
      title: getText('Excellence technique', 'التميز التقني', 'Technical excellence'),
      desc: getText('Standards élevés et expertise reconnue', 'معايير عالية وخبرة معترف بها', 'High standards and recognized expertise')
    },
    { 
      title: getText('Agilité', 'المرونة', 'Agility'),
      desc: getText('Réactivité et adaptation aux besoins', 'الاستجابة والتكيف مع الاحتياجات', 'Responsiveness and adaptation to needs')
    },
    { 
      title: getText('Partenariat', 'الشراكة', 'Partnership'),
      desc: getText('Relations durables et co-construction', 'علاقات دائمة وبناء مشترك', 'Lasting relationships and co-construction')
    },
    { 
      title: getText('Innovation', 'الابتكار', 'Innovation'),
      desc: getText('R&D continue et veille technologique', 'بحث وتطوير مستمر ومراقبة تقنية', 'Continuous R&D and technology watch')
    }
  ];

  return (
    <div data-testid="group-page" className="bg-[#F8F6F3]">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="group-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        
        <div className="container-main relative z-10 py-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-marquee-label text-white/80 mb-6 block">
              {getText('GROUPE D.ONE', 'مجموعة دي.وان', 'D.ONE GROUP')}
            </span>
            <h1 className="text-hero text-white mb-8">
              {getText('Le groupe', 'المجموعة', 'The group')}
            </h1>
            <p className="text-xl text-white/60 max-w-2xl leading-relaxed">
              {getText(
                'Conception et industrialisation de solutions télécom et retail. Basé en France, actif partout.',
                'تصميم وتصنيع حلول الاتصالات والتجزئة. مقره في فرنسا، نشط في كل مكان.',
                'Design and industrialization of telecom and retail solutions. Based in France, active everywhere.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text={getText('notre approche', 'نهجنا', 'our approach')} />

      {/* About Section with asymmetric images */}
      <section className="section-xl" data-testid="about-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="rounded-[30px] overflow-hidden">
                <img src={OFFICE_IMG} alt="Office" className="w-full aspect-[4/3] object-cover" />
              </div>
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute -bottom-10 -right-6 lg:-right-12 w-48 lg:w-64 rounded-[24px] overflow-hidden shadow-2xl border-4 border-white"
              >
                <img src={TEAM_IMG} alt="Team" className="w-full aspect-square object-cover" />
              </motion.div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-marquee-label mb-6 block">
                {getText('QUI SOMMES-NOUS', 'من نحن', 'WHO WE ARE')}
              </span>
              <h2 className="text-display mb-8">
                {getText(
                  'Leader du sourcing télécom & retail',
                  'رائد توريد الاتصالات والتجزئة',
                  'Leading telecom & retail sourcing'
                )}
              </h2>
              <p className="text-body-lg mb-8">
                {getText(
                  "Notre engagement envers l'excellence et l'innovation guide chacune de nos actions. Nous transformons vos idées en produits concrets, de la conception à la mise en marché.",
                  'التزامنا بالتميز والابتكار يوجه كل أعمالنا. نحول أفكاركم إلى منتجات ملموسة، من التصميم إلى طرحها في السوق.',
                  'Our commitment to excellence and innovation guides each of our actions. We transform your ideas into concrete products, from design to market launch.'
                )}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 py-8 border-y border-gray-200">
                {[
                  { value: '10+', label: getText("ans d'expérience", 'سنوات خبرة', 'years experience') },
                  { value: '50+', label: getText('projets livrés', 'مشاريع منجزة', 'projects delivered') },
                  { value: '15+', label: getText('pays couverts', 'دولة مغطاة', 'countries') }
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-bold text-[#f4b00f]">{stat.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text={getText('nos piliers', 'أعمدتنا', 'our pillars')} reverse />

      {/* Pillars Section - Dark with glass cards */}
      <section className="section-xl bg-[#0A0A0A] relative overflow-hidden" data-testid="pillars-section">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#f4b00f]/5 rounded-full blur-3xl" />
        
        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-marquee-label text-white/60 mb-6 block">
              {getText('NOTRE APPROCHE', 'نهجنا', 'OUR APPROACH')}
            </span>
            <h2 className="text-display text-white">
              {getText('Trois piliers', 'ثلاثة أعمدة', 'Three pillars')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((pillar, index) => (
              <motion.div 
                key={pillar.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="card-glass-dark p-8 group"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-6xl font-bold text-white/10 group-hover:text-[#f4b00f]/20 transition-colors">
                    {pillar.number}
                  </span>
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ffcc4d] via-[#f4b00f] to-[#d99a00] flex items-center justify-center">
                    <pillar.icon className="w-6 h-6 text-black" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{pillar.title}</h3>
                <p className="text-white/50">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section - Glass cards on image background */}
      <section className="section-xl relative overflow-hidden" data-testid="values-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img src={TEAM_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-marquee-label text-white/60 mb-6 block">
                {getText('NOS VALEURS', 'قيمنا', 'OUR VALUES')}
              </span>
              <h2 className="text-display text-white mb-6">
                {getText('Ce qui nous définit', 'ما يميزنا', 'What defines us')}
              </h2>
              <p className="text-white/60 text-lg">
                {getText(
                  "Il ne s'agit pas seulement de créer quelque chose de bien ; il s'agit de concevoir, innover et collaborer pour forger des expériences remarquables.",
                  'الأمر لا يتعلق فقط بإنشاء شيء جيد؛ بل بالتصميم والابتكار والتعاون لصياغة تجارب استثنائية.',
                  "It's not just about creating something good; it's about designing, innovating, and collaborating to forge remarkable experiences."
                )}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-5"
            >
              {values.map((value, index) => (
                <motion.div 
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="feature-item"
                >
                  <CheckCircle className="w-8 h-8 text-[#f4b00f] mb-4" />
                  <h4 className="font-semibold mb-2">{value.title}</h4>
                  <p className="text-sm text-gray-500">{value.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-xl" data-testid="group-cta">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[40px]"
            style={{ borderRadius: '40px 100px 40px 40px' }}
          >
            <div className="absolute inset-0 bg-[#0A0A0A]" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#f4b00f]/10 rounded-full blur-3xl" />
            
            <div className="relative z-10 p-12 md:p-20 text-center">
              <h2 className="text-3xl md:text-5xl font-semibold text-white mb-6">
                {getText('Travaillons ensemble', 'لنعمل معًا', "Let's work together")}
              </h2>
              <p className="text-white/50 mb-10 max-w-xl mx-auto">
                {getText(
                  'Vous avez un projet télécom ou retail tech ? Discutons de vos besoins.',
                  'هل لديك مشروع في مجال الاتصالات أو تقنية التجزئة؟ دعنا نناقش احتياجاتك.',
                  "Do you have a telecom or retail tech project? Let's discuss your needs."
                )}
              </p>
              <Link to="/contact" className="btn-primary">
                {getText('Nous contacter', 'اتصل بنا', 'Contact us')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default GroupPage;
